/**
 * Get panel height from current rendered layout.
 *
 * @param {HTMLElement} panel Accordion panel.
 * @return {number} Panel height in pixels.
 */
const getPanelHeight = ( panel ) => panel.getBoundingClientRect().height;
const shouldUseInstantTransition = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

/**
 * Build runtime-only accordion panel id.
 *
 * @return {string} Runtime panel id.
 */
const buildRuntimePanelId = () => {

	if ( window.crypto?.randomUUID ) {

		return `wombat-media-accordion-panel-${window.crypto.randomUUID()}`;

	}

	return `wombat-media-accordion-panel-${Date.now()}-${Math.random().toString( 36 ).slice( 2, 10 )}`;

};

/**
 * Clear pending panel animation state.
 *
 * @param {HTMLElement} panel Accordion panel.
 * @return {void}
 */
const clearPanelAnimationState = ( panel ) => {

	if ( panel.__wombatAccordionRafId ) {

		window.cancelAnimationFrame( panel.__wombatAccordionRafId );
		panel.__wombatAccordionRafId = null;

	}

	if ( panel.__wombatAccordionTransitionEndHandler ) {

		panel.removeEventListener( 'transitionend', panel.__wombatAccordionTransitionEndHandler );
		panel.__wombatAccordionTransitionEndHandler = null;

	}

};

/**
 * Animate panel to target height.
 *
 * @param {HTMLElement} panel Accordion panel.
 * @param {number} targetHeight Target height in px.
 * @param {Function} onEnd End callback.
 * @param {boolean} instant Whether to skip transition.
 * @param {string|null} targetPaddingBottom Target bottom padding value.
 * @return {void}
 */
const animatePanelHeight = ( panel, targetHeight, onEnd, instant = false, targetPaddingBottom = null ) => {

	clearPanelAnimationState( panel );
	const startHeight = getPanelHeight( panel );
	panel.style.height = `${startHeight}px`;

	if ( instant ) {

		panel.style.height = `${targetHeight}px`;
		if ( targetPaddingBottom !== null ) {

			panel.style.paddingBottom = targetPaddingBottom;

		}
		onEnd();
		return;

	}

	panel.__wombatAccordionRafId = window.requestAnimationFrame( () => {

		panel.style.height = `${targetHeight}px`;
		if ( targetPaddingBottom !== null ) {

			panel.style.paddingBottom = targetPaddingBottom;

		}

	} );

	/**
	 * Finalize panel height transition.
	 *
	 * @param {TransitionEvent} event Transition event payload.
	 * @return {void}
	 */
	const handleTransitionEnd = ( event ) => {

		if ( event.propertyName !== 'height' ) {

			return;

		}

		clearPanelAnimationState( panel );
		onEnd();

	};

	panel.__wombatAccordionTransitionEndHandler = handleTransitionEnd;
	panel.addEventListener( 'transitionend', handleTransitionEnd );

};

/**
 * Expand selected accordion panel.
 *
 * @param {HTMLElement} item Accordion item.
 * @param {HTMLElement|null} panel Accordion panel.
 * @param {boolean} instant Whether to skip transition.
 * @return {void}
 */
const openPanel = ( item, panel, instant = false ) => {

	if ( !panel ) {

		return;

	}

	panel.hidden = false;
	panel.removeAttribute( 'aria-hidden' );
	panel.inert = false;
	item.classList.add( 'is-active' );

	panel.style.transition = 'none';
	panel.style.height = 'auto';
	panel.style.paddingBottom = '';
	const expandedHeight = getPanelHeight( panel );
	const expandedPaddingBottom = window.getComputedStyle( panel ).paddingBottom;
	panel.style.height = '0px';
	panel.style.paddingBottom = '0px';
	panel.getBoundingClientRect();
	panel.style.transition = '';

	animatePanelHeight(
		panel,
		expandedHeight,
		() => {

			panel.style.height = 'auto';
			panel.style.paddingBottom = '';

		},
		instant,
		expandedPaddingBottom
	);

};

/**
 * Collapse non-active accordion panel.
 *
 * @param {HTMLElement} item Accordion item.
 * @param {HTMLElement|null} panel Accordion panel.
 * @param {boolean} instant Whether to skip transition.
 * @return {void}
 */
const closePanel = ( item, panel, instant = false ) => {

	if ( !panel ) {

		return;

	}

	panel.hidden = false;
	clearPanelAnimationState( panel );

	panel.style.transition = 'none';
	panel.style.height = `${getPanelHeight( panel )}px`;
	panel.style.paddingBottom = window.getComputedStyle( panel ).paddingBottom;
	panel.getBoundingClientRect();
	panel.style.transition = '';

	item.classList.remove( 'is-active' );

	animatePanelHeight(
		panel,
		0,
		() => {

			panel.style.height = '0px';
			panel.style.paddingBottom = '';
			panel.hidden = true;
			panel.setAttribute( 'aria-hidden', 'true' );
			panel.inert = true;

		},
		instant,
		'0px'
	);

};

/**
 * Activates accordion item without moving any media nodes in DOM.
 *
 * @param {HTMLElement[]} items Accordion items.
 * @param {HTMLElement} activeItem Active accordion item.
 * @param {boolean} instant Whether to skip transition.
 * @return {void}
 */
const activateItem = ( items, activeItem, instant = false ) => {

	items.forEach( ( item ) => {

		const trigger = item.querySelector( '[data-wombat-media-accordion-trigger]' );
		const panel = item.querySelector( '[data-wombat-media-accordion-panel]' );
		const isActive = item === activeItem;

		if ( trigger ) {

			trigger.setAttribute( 'aria-expanded', isActive ? 'true' : 'false' );

		}

		if ( isActive ) {

			openPanel( item, panel, instant );

		} else {

			closePanel( item, panel, instant );

			const videos = item.querySelectorAll( 'video' );
			videos.forEach( ( video ) => {

				if ( !video.paused ) {

					video.pause();

				}

			} );

			const iframes = item.querySelectorAll( 'iframe' );
			iframes.forEach( ( iframe ) => {

				const src = iframe.getAttribute( 'src' );

				if ( src ) {

					iframe.setAttribute( 'src', src );

				}

			} );

		}

	} );

};

document.querySelectorAll( '[data-wombat-media-accordion]' ).forEach( ( accordion ) => {

	const items = Array.from( accordion.querySelectorAll( '[data-wombat-media-accordion-item]' ) );

	if ( !items.length ) {

		return;

	}

	accordion.classList.add( 'is-enhanced' );
	const triggerElements = items
		.map( ( accordionItem ) => accordionItem.querySelector( '[data-wombat-media-accordion-trigger]' ) )
		.filter( Boolean );

	items.forEach( ( item ) => {

		const panel = item.querySelector( '[data-wombat-media-accordion-panel]' );
		const trigger = item.querySelector( '[data-wombat-media-accordion-trigger]' );

		if ( panel ) {

			const panelId = buildRuntimePanelId();
			panel.id = panelId;

			if ( trigger ) {

				trigger.setAttribute( 'aria-controls', panelId );

			}

		}

		if ( trigger ) {

			trigger.addEventListener( 'click', () => {

				if ( item.classList.contains( 'is-active' ) ) {

					return;

				}

				activateItem( items, item, shouldUseInstantTransition );

			} );

			trigger.addEventListener( 'keydown', ( event ) => {

				const triggerIndex = triggerElements.indexOf( trigger );
				const isLast = triggerIndex === triggerElements.length - 1;
				const isFirst = triggerIndex === 0;

				if ( event.key === 'ArrowDown' ) {

					event.preventDefault();
					const nextTrigger = isLast ? triggerElements[0] : triggerElements[triggerIndex + 1];
					nextTrigger?.focus();
					return;

				}

				if ( event.key === 'ArrowUp' ) {

					event.preventDefault();
					const previousTrigger = isFirst ? triggerElements[triggerElements.length - 1] : triggerElements[triggerIndex - 1];
					previousTrigger?.focus();
					return;

				}

				if ( event.key === 'Home' ) {

					event.preventDefault();
					triggerElements[0]?.focus();
					return;

				}

				if ( event.key === 'End' ) {

					event.preventDefault();
					triggerElements[triggerElements.length - 1]?.focus();

				}

			} );

		}

	} );

	activateItem( items, items[0], true );

} );
